/* eslint-disable @next/next/no-img-element */
import {
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TuneIcon from "@mui/icons-material/Tune";
import { colors } from "common";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import _ from "lodash";
import { Theme, useTheme } from "@mui/material/styles";
import modules from "modules";
import { useAppSelector } from "hooks/use-app-selector";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useCallback, useEffect, useMemo, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import ShopLayout1 from "components/layouts/ShopLayout1";
import {
  TableCellCategory,
  TableCellName,
  TableCellPromoPrice,
} from "components/pricelist";
import { PriceListApi } from "modules/pricelist/api";
import { Download } from "@mui/icons-material";
import { PriceListSearchBar } from "../../src/components/pricelist/PriceListSearchBar";
import { Span } from "components/Typography";
import { CategoryItem } from "modules/category/types";
import LazyImage from "components/LazyImage";
import { PriceListPayloadV2 } from "modules/pricelist/types";
import { FlexBox } from "components/flex-box";
import { useIsMobile } from "hooks";
import { CategListItem } from "components/pricelist/CategListItem";
import { SelectValue } from "components/pricelist/SelectValue";
import { initialPayload } from "modules/pricelist";
import { useRouter } from "next/router";
import { TableCellMobile } from "components/pricelist/TableCellMobile";
import useWindowSize from "hooks/useWindowSize";

const columns = [
  {
    id: 1,
    label: "COMPONENT",
    minWidth: 170,
  },
  { id: 2, label: "NAME", minWidth: 170 },
  {
    id: 3,
    label: "PROMO PRICE",
    minWidth: 170,
    textColor: "#E94560",
  },
];

const maxWidthOfTable = 1800;

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {
        margin: 30,
        paddingBottom: 20,
      },
      table: {
        borderTop: "1px solid black",
        borderLeft: "1px solid black",
        borderColor: colors.primary,
        maxWidth: maxWidthOfTable,
      },
      tableRow: {
        "&:hover": {
          backgroundColor: colors.pink[50],
        },
      },
      tableCellBorder: {
        borderRight: "0.5px solid black",
        borderColor: colors.primary,
        fontWeight: 900,
        fontSize: 16,
      },
      tableHeader: {
        backgroundColor: colors.orange[200],
      },
      pagination: {
        position: "absolute",
        marginTop: 90,
        marginBottom: 200,
      },
      fab: {
        position: "absolute",
        marginTop: 20,
        marginBottom: 200,
        fontWeight: 900,
      },
      row: {
        flexDirection: "row",
        display: "flex",
      },
      pricesChangesAlert: {
        backgroundColor: "#CDEBF8",
        maxWidth: maxWidthOfTable,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      pricesChangesAlertTxt: {
        fontSize: 18,
        [theme.breakpoints.down("md")]: {
          fontSize: 15,
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: 10,
        },
      },
      dlButton: {
        backgroundColor: colors.primary,
        maxWidth: maxWidthOfTable,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
        color: colors.white,
        cursor: "pointer",
        fontWeight: 700,
        fontSize: 17,
        [theme.breakpoints.down("md")]: {
          fontSize: 15,
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: 13,
        },
      },
      priceListDateTxt: {
        color: "black",
        fontWeight: 900,
        fontSize: "30px",
        [theme.breakpoints.down("md")]: {
          fontSize: 15,
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "12.5px",
        },
      },
      date: {
        color: "#2F80ED",
      },
      time: { color: "#CF151A" },
      logoQuoteImg: {
        width: undefined,
        height: 70,
        marginRight: 10,
        [theme.breakpoints.down("md")]: {
          height: 0,
          marginRight: 0,
        },
      },
      logoImg: {
        width: undefined,
        height: 70,
        marginLeft: 10,
        [theme.breakpoints.down("md")]: {
          height: 0,
          marginLeft: 0,
        },
      },
      mobileTable: {
        marginTop: 1,
      },
      searchBar: {
        maxWidth: maxWidthOfTable,
      },
    };
  },
  { index: 999 }
);

const { actions: categoryActions } = modules.category;

const { selectors: priceListSelectors, actions: pricelistActions } =
  modules.priceList;

const PriceListView = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useAppDispatch();
  const width = useWindowSize();
  const isMobile = width <= 912; // Set to 912 to consider more tablet dimensions
  const router = useRouter();
  const { replace, query } = useRouter();

  const [searchInputValue, setSearchInputValue] = useState("");
  const [isShowDisclaimer, setIsShowDisclaimer] = useState<boolean>(true);
  const [categVal, setCategVal] = useState<string[]>(["0"]);
  const [categoryOptions, setCategoryOptions] = useState<CategoryItem[]>([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const priceListData = useAppSelector(
    priceListSelectors.selectPriceListEUData
  );
  const priceList = useAppSelector(priceListSelectors.selectPriceListEU);
  const isLoading = useAppSelector(priceListSelectors.selectPriceListLoading);
  const paginationMeta = useAppSelector(
    priceListSelectors.selectPaginationMeta
  );
  const payload = useAppSelector(priceListSelectors.selectPayload);
  const sortDir = useAppSelector(priceListSelectors.selectSortDir);

  const dateString = useMemo(() => {
    const str = priceList?.pricelist_date ?? "";
    const regex = /(\D+,|\w+\s\d{1,2},\s\d{4})|\d{1,2}:\d{2}\s\w{2}/gi;

    if (str) {
      const matches = str.match(regex);
      return matches;
    } else {
      return;
    }
  }, [priceList]);

  const handleSetPayload = useCallback(
    (payload: PriceListPayloadV2) => {
      dispatch(pricelistActions.setPayload(payload));
    },
    [dispatch]
  );

  const getPriceListEU = useCallback(async () => {
    await dispatch(pricelistActions.getEUpriceListV2Thunk());
  }, [dispatch]);

  const handleChangeKeyword = useCallback(
    (event: any) => {
      const keyword = event.target.value;
      const updatedPayload = { ...payload, keyword: keyword };

      handleSetPayload(updatedPayload);
      setSearchInputValue(keyword);
    },
    [handleSetPayload, payload]
  );

  const handleChangePage = useCallback(
    (_, p: number) =>
      replace({ query: { ...query, page: p } }, undefined, { shallow: true }),
    [query, replace]
  );

  const handleChangeSortDir = useCallback(
    (event: any) => {
      const updatedPayload = { ...payload, sort: event.target.value };

      replace(
        { query: { ...query, sort_direction: event.target.value } },
        undefined,
        { shallow: true }
      );

      handleSetPayload(updatedPayload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [priceList]
  );

  const handleSearchKeyword = useCallback(() => {
    if (isLoading) {
      return;
    }

    if (searchInputValue.length >= 1) {
      replace({ query: { ...query, keyword: searchInputValue } }, undefined, {
        shallow: true,
      });
    } else {
      let updatedQuery = query;
      delete updatedQuery["keyword"];
      replace({ query: updatedQuery }, undefined, {
        shallow: true,
      });
    }

    handleSetPayload({ ...payload, page: 1 });
    getPriceListEU();
  }, [
    getPriceListEU,
    handleSetPayload,
    isLoading,
    payload,
    query,
    replace,
    searchInputValue,
  ]);

  const downloadPDF = async () => {
    PriceListApi.downloadEUpriceListPDF().then((resp) => {
      if (resp?.data && resp?.data instanceof Blob) {
        const url = URL.createObjectURL(resp?.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = "PCworth-product-pricelist.pdf";
        link.click();
        return resp;
      }
    });
  };

  const getAllCategories = async () => {
    const response = unwrapResult(
      await dispatch(categoryActions.getAllCategoriesThunk())
    );
    setCategoryOptions(response?.originalData?.data);
  };

  const handleChangeCategory = (e: any) => {
    let updatedCategArr = e.target.value;
    let updatedPayload = { ...initialPayload };

    if (categVal.includes("0") && updatedCategArr.length > 1) {
      updatedCategArr = updatedCategArr.filter((i) => i !== "0");
    } else if (updatedCategArr.includes("0") && !categVal.includes("0")) {
      updatedCategArr = ["0"];
    } else if (updatedCategArr.length === 0) {
      updatedCategArr = ["0"];
    }

    setCategVal(updatedCategArr);
    setSearchInputValue("");
    updatedPayload.category = updatedCategArr.join(",");
    handleSetPayload(updatedPayload);

    if (updatedCategArr.length >= 1 && updatedCategArr[0] !== "0") {
      replace(
        { query: { ...query, categories: updatedCategArr.join(",") } },
        undefined,
        {
          shallow: true,
        }
      );
    } else {
      let updatedQuery = query;
      delete updatedQuery["categories"];

      replace({ query: updatedQuery }, undefined, {
        shallow: true,
      });
    }
  };

  const handleToggleFilter = useCallback(() => {
    setIsFiltersOpen(!isFiltersOpen);
  }, [isFiltersOpen]);

  const drawerOptClick = useCallback(
    (val: string) => {
      let updatedCategArr = categVal;
      let updatedPayload = { ...initialPayload };

      if (updatedCategArr.includes(val)) {
        updatedCategArr = updatedCategArr.filter((i) => i !== val);
        if (!updatedCategArr[0]) {
          updatedCategArr = ["0"];
        }
      } else if (val === "0") {
        updatedCategArr = ["0"];
      } else {
        updatedCategArr = [...updatedCategArr, val].filter((i) => i !== "0");
      }

      setCategVal(updatedCategArr);
      // Reset state values for ui when changing categories
      updatedPayload.category = updatedCategArr.join(",");
      handleSetPayload(updatedPayload);
      setSearchInputValue("");

      if (updatedCategArr[0] !== "0") {
        replace(
          { query: { ...query, categories: updatedCategArr?.join(",") ?? "" } },
          undefined,
          {
            shallow: true,
          }
        );
      } else {
        let updatedQuery = query;
        delete updatedQuery["categories"];
        replace({ query: updatedQuery }, undefined, {
          shallow: true,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categVal]
  );

  useEffect(() => {
    getAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (router.isReady) {
      let payload = { ...initialPayload };
      const queryProps = Object.entries(router.query);

      queryProps.forEach(([key, value]) => {
        switch (key) {
          case "keyword":
            const keyword = value as string;
            setSearchInputValue(keyword);
            payload.keyword = keyword;
            break;
          case "sort_direction":
            const sortDir = value as string;
            payload.sort = sortDir;
            break;
          case "categories":
            const categories = value as string;
            setCategVal(categories?.split(",") ?? []);
            payload.category = categories;
            break;
          case "page":
            const page = value as string;
            payload.page = +page;
            break;
        }
      });

      handleSetPayload(payload);
      getPriceListEU();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, router.isReady]);

  return (
    <ShopLayout1 title="Price List">
      <div
        className={classes.root}
        title="Price List"
        style={{ marginBottom: 100 }}
      >
        <Container
          sx={{
            width: "100%",
            "@media (max-width: 1280px)": {
              px: "0px !important",
            },
            mb: "30px",
          }}
        >
          <Grid
            container
            sx={{
              maxWidth: maxWidthOfTable,
              marginTop: 7,
            }}
          >
            <Grid
              item
              lg={2}
              xs={0}
              md={2}
              sx={{ display: isMobile ? "none" : "block" }}
            >
              <Select
                multiple
                fullWidth
                value={categVal}
                onChange={handleChangeCategory}
                autoWidth
                renderValue={(selected) => <SelectValue selected={selected} />}
                SelectDisplayProps={{ style: { display: "flex" } }}
              >
                <MenuItem value={"0"}>
                  <LazyImage
                    src="/assets/images/icons/Vector.png"
                    width={20}
                    height={20}
                    sx={{ display: "inline-block" }}
                  />
                  <Typography
                    sx={{
                      color: categVal.includes("0") ? colors.error : "initial",
                      display: "inline-block",
                      mr: "10em",
                      ml: "1em",
                    }}
                  >
                    All Products
                  </Typography>
                  {categVal.includes("0") && (
                    <CheckIcon sx={{ color: colors.error }} />
                  )}
                </MenuItem>
                {categoryOptions[0] &&
                  categoryOptions.map((i) => (
                    <MenuItem
                      key={i.id}
                      value={i.name}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        },
                      }}
                    >
                      <LazyImage
                        src={i?.icon_sidedrawer}
                        width={20}
                        height={20}
                        sx={{ display: "inline-block" }}
                      />
                      <Typography
                        ml="1em"
                        display="inline-block"
                        sx={{
                          color: categVal.includes(i?.name)
                            ? colors.error
                            : "initial",
                        }}
                      >
                        {i?.name}
                      </Typography>
                      {categVal.includes(i?.name) && (
                        <CheckIcon sx={{ color: colors.error }} />
                      )}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item lg md sm xs={9}>
              <PriceListSearchBar
                searchKeyword={handleSearchKeyword}
                value={searchInputValue}
                onChangeKeyword={handleChangeKeyword}
                className={classes.searchBar}
              />
            </Grid>
            <Grid
              item
              lg={2}
              md={2}
              sm={2}
              xs={0}
              sx={{ display: isMobile ? "none" : "block", pl: "1em" }}
            >
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  label="Sort By"
                  fullWidth
                  value={sortDir}
                  onChange={handleChangeSortDir}
                >
                  <MenuItem value={"asc"}>Lowest Price</MenuItem>
                  <MenuItem value={"desc"}>Highest Price</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={3}
              sm={2}
              sx={{ display: { lg: "none", md: "none" }, pl: "10px" }}
            >
              <a onClick={handleToggleFilter}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    justifyContent: "end",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <Typography sx={{ mr: ".8em" }} fontWeight="600">
                    Filter
                  </Typography>
                  <TuneIcon />
                </Box>
              </a>
            </Grid>
          </Grid>
          {isLoading ? <LinearProgress color="secondary" /> : null}
          <Box
            sx={{
              maxWidth: maxWidthOfTable,
              display: "flex",
              flex: 1,
              height: isMobile ? 40 : 80,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: "black",
                fontWeight: 900,
                fontSize: { lg: "30px", md: 15, xs: "12px" },
              }}
            >
              {dateString && (
                <>
                  <Span> {dateString[0]}</Span>
                  <Span className={classes.time}> {dateString[1]}</Span>
                  <Span className={classes.date}> {dateString[2]}</Span>
                </>
              )}
            </Typography>
          </Box>
          {/* Disclaimer and Download Button position is swapped on mobile view on figma reference */}
          {isMobile ? (
            <>
              <Box
                className={classes.dlButton}
                sx={{
                  marginBottom: "10px",
                }}
                onClick={downloadPDF}
              >
                <Download />
                <Typography className={classes.pricesChangesAlertTxt}>
                  DOWNLOAD PRODUCT(S) PRICELIST
                </Typography>
              </Box>
              <Box
                style={{
                  marginBottom: isShowDisclaimer ? 10 : 0,
                  backgroundColor: "#CDEBF8",
                  maxWidth: maxWidthOfTable,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Grid
                  container
                  sx={{
                    p: "1em",
                    borderBottom: ".3em solid rgba(47, 128, 237, 1)",
                    justifyContent: "center",
                    display: isShowDisclaimer ? "flex" : "none",
                  }}
                >
                  <Grid item xs={1}>
                    <LazyImage
                      src="/assets/images/icons/info-vector.png"
                      width={26}
                      height={26}
                    />
                  </Grid>
                  <Grid
                    item
                    lg
                    xs
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isMobile ? null : (
                      <LazyImage
                        src="/assets/images/icons/info-vector.png"
                        width={26}
                        height={26}
                      />
                    )}
                    <Typography
                      className={classes.pricesChangesAlertTxt}
                      sx={{
                        textAlign: isMobile ? "left" : "center",
                        marginLeft: isMobile ? "10px" : "20px",
                      }}
                    >
                      <Span fontWeight={900}>Price</Span> and
                      <Span fontWeight={900}> stocks(s) </Span>
                      are subject to change without any prior notice and
                      shipping fees not yet included.
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    lg={1}
                    xs={1}
                    sx={{
                      display: "flex",
                      alignSelf: isMobile ? "start" : "unset",
                    }}
                  >
                    <Button
                      sx={{ p: 0 }}
                      onClick={() => setIsShowDisclaimer(false)}
                    >
                      <CloseIcon
                        sx={{ fontSize: isMobile ? "1.8em" : "2.7em" }}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <>
              <Box
                style={{
                  marginBottom: isShowDisclaimer ? 20 : 0,
                  backgroundColor: "#CDEBF8",
                  maxWidth: maxWidthOfTable,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Grid
                  container
                  sx={{
                    p: "1em",
                    borderBottom: ".3em solid rgba(47, 128, 237, 1)",
                    justifyContent: "center",
                    display: isShowDisclaimer ? "flex" : "none",
                  }}
                >
                  <Grid
                    item
                    lg
                    xs
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <LazyImage
                      src="/assets/images/icons/info-vector.png"
                      width={26}
                      height={26}
                    />
                    <Typography
                      className={classes.pricesChangesAlertTxt}
                      sx={{
                        textAlign: isMobile ? "left" : "center",
                        marginLeft: "20px",
                      }}
                    >
                      <Span fontWeight={900}>Price</Span> and
                      <Span fontWeight={900}> stocks(s) </Span>
                      are subject to change without any prior notice and
                      shipping fees not yet included.
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    lg={1}
                    xs={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      alignSelf: isMobile ? "start" : "unset",
                    }}
                  >
                    <Button
                      sx={{
                        p: 0,
                      }}
                      onClick={() => setIsShowDisclaimer(false)}
                    >
                      <CloseIcon
                        sx={{ fontSize: isMobile ? "2em" : "2.7em" }}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              <Box
                className={classes.dlButton}
                sx={{
                  marginBottom: "20px",
                }}
                onClick={downloadPDF}
              >
                <Download />
                <Typography
                  sx={{ fontSize: "1em !important" }}
                  className={classes.pricesChangesAlertTxt}
                >
                  DOWNLOAD PRODUCT(S) PRICELIST
                </Typography>
              </Box>
            </>
          )}
          {/* tables */}
          {isMobile ? (
            <Table
              size="small"
              className={clsx(classes.mobileTable, classes.table)}
            >
              <TableBody>
                {(priceListData || []).map((row, i) => (
                  <TableRow
                    className={classes.tableRow}
                    key={row.id}
                    tabIndex={-1}
                  >
                    <TableCellMobile
                      index={i}
                      data={row}
                      categories={categoryOptions}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      style={{
                        minWidth: column.minWidth,
                      }}
                      className={clsx(
                        classes.tableHeader,
                        classes.tableCellBorder
                      )}
                    >
                      <Typography fontWeight={900} fontSize={"medium"}>
                        {column.label}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(priceListData || []).map((row, i) => {
                  return (
                    <TableRow
                      className={classes.tableRow}
                      key={row.id}
                      tabIndex={-1}
                    >
                      <TableCellCategory
                        categories={categoryOptions}
                        index={i}
                        data={row}
                      />
                      <TableCellName
                        index={i}
                        data={row}
                        categories={categoryOptions}
                      />
                      <TableCellPromoPrice data={row} />
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <FlexBox justifyContent={"end"} px="1em">
            <Fab
              size="large"
              color="primary"
              variant="extended"
              className={classes.fab}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <ArrowUpwardIcon />
              Scroll Up
            </Fab>
          </FlexBox>
          <FlexBox justifyContent={"end"}>
            <Pagination
              className={classes.pagination}
              count={paginationMeta?.last_page ?? 1}
              page={paginationMeta?.current_page ?? 1}
              onChange={handleChangePage}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />
          </FlexBox>
        </Container>
        {/* Closing parent */}
      </div>
      {isMobile && (
        <Drawer open={isFiltersOpen} onClose={handleToggleFilter}>
          <FlexBox
            sx={{
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "26px",
                display: "flex",
                alignItems: "center",
              }}
            >
              Filter
            </Typography>
            <Button onClick={handleToggleFilter}>
              <CloseIcon />
            </Button>
          </FlexBox>
          <Divider sx={{ borderBottom: "1px solid rgb(41, 41, 41, .2)" }} />
          <List>
            <ListItemButton onClick={() => drawerOptClick("0")}>
              <LazyImage
                src="/assets/images/icons/Vector.png"
                width={20}
                height={20}
              />
              <ListItemText
                sx={{
                  color: categVal.includes("0") ? "#E94560" : "initial",
                  opacity: 0.8,
                  ml: "1em",
                }}
              >
                All Products
              </ListItemText>
              {categVal.includes("0") ? (
                <CheckIcon sx={{ color: "#E94560" }} />
              ) : null}
            </ListItemButton>
            {categoryOptions[0] &&
              categoryOptions.map((i, ind) => (
                <CategListItem
                  categVal={categVal}
                  key={ind}
                  i={i}
                  onClick={() => drawerOptClick(i.name)}
                />
              ))}
          </List>
          {/* <Select
            fullWidth
            value={categVal}
            onChange={handleChangeCategory}
            autoWidth
            label="components"
            SelectDisplayProps={{
              style: {
                display: "flex",
              },
            }}
          >
            <MenuItem value={0}>All</MenuItem>
            {categoryOptions[0] &&
              categoryOptions.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  <LazyImage
                    src={i.icon_sidedrawer}
                    width={20}
                    height={20}
                    sx={{ display: "inline-block" }}
                  />
                  <Typography ml="1em" display="inline-block">
                    {i.name}
                  </Typography>
                </MenuItem>
              ))}
          </Select> */}
        </Drawer>
      )}
    </ShopLayout1>
  );
};

export default PriceListView;
